/*
 *   File : list.js
 *   Author : https://evoqins.com
 *   Description : Switch/SWP/STP/NFO transactions list
 *   Integrations : 'react-toastify'
 *   Version : 1.0.0
*/

import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { Modal } from 'bootstrap/dist/js/bootstrap.min';

import { CustomSearchInput, CustomSelectBox, DateRangePicker } from '../../Components/FormElements';
import { PrimaryButton, SecondaryButton, TextButton } from '../../Components/Buttons';
import { CustomDataTable } from '../../Components/DataTable';
import { Loader } from '../../Components/Others';
import { Icon } from '../../Components/Icon';
import { OtherTransactionDetailModal, NfoDetailModal, SystematicPlanOrderModal } from '../../Components/Modal';
import { APIService } from '../../Service';
import EmptyScreen from '../../Components/Others/empty-screen';
import color from '../../Style/color.scss';
import { TAB_TITLES, getActiveTransactionTab, getListInvestmentType, getListOrderType, getOtherTransactionsListColumns, isNfoTab, isSchemeFilterTab } from './transaction-table-config';

const resolveSchemeOption = (options = [], id, label) => {
    if (id != null) {
        const byId = options.find((item) => String(item.value) === String(id));
        if (byId) return byId;
    }

    if (label) {
        const normalized = String(label).trim().toLowerCase();
        const byLabel = options.find((item) => String(item.label || "").trim().toLowerCase() === normalized);
        if (byLabel) return byLabel;
    }

    if (id != null || label) {
        return { value: id ?? null, label: label || "-" };
    }

    return null;
};

const hasIncomingSchemeFilter = (navState) => {
    if (!navState) return false;
    const tab = getActiveTransactionTab(navState.tab_index);
    if (isSchemeFilterTab(tab)) return navState.mf_id != null || !!navState.scheme;
    if (tab === 3) return navState.mf_id != null || navState.target_mf_id != null || !!navState.from_scheme || !!navState.to_scheme;
    return false;
};

const OtherTransactionsList = () => {
    const PROFILE_DATA = useSelector((store) => store.Reducer.PROFILE_DATA);
    const hasOrderDownloadAccess = PROFILE_DATA?.modules?.some(
        (module) => module?.name?.toLowerCase() === "order download"
    );

    const { state } = useLocation();
    const navigate = useNavigate();

    const isRendered = useRef(true);
    const isClearRef = useRef(false);
    const scrollRef = useRef(null);
    const pendingIncomingSchemeRef = useRef(hasIncomingSchemeFilter(state));

    const [searchValue, setSearchValue] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedTab, setSelectedTab] = useState(-1);
    const [rayiPaymentStatus, setRayiPaymentStatus] = useState(null);
    const [selectedOrderStatus, setSelectedOrderStatus] = useState(null);
    const [searched, setSearched] = useState(false);
    const [loader, setLoader] = useState(true);
    const [filterLoader, setFilterLoader] = useState(true);
    const [toggleFilter, setToggleFilter] = useState(false);
    const [orderListData, setOrderListData] = useState({});
    const [orderFilterData, setOrderFilterData] = useState({});
    const [downloadLoader, setDownloadLoader] = useState(false);
    const [orderDate, setOrderDate] = useState(null);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [selectedScheme, setSelectedScheme] = useState(null);
    const [selectedFromScheme, setSelectedFromScheme] = useState(null);
    const [selectedToScheme, setSelectedToScheme] = useState(null);

    useEffect(() => {
        if (isClearRef.current === true) {
            setLoader(true);
            if (filterLoader === false) {
                _orderList();
            }
        }
    }, [isClearRef.current]);

    useEffect(() => {
        if (isRendered.current === true) {
            _filterOrder();
        }
        isRendered.current = false;
        window.scrollTo({
            top: 0, behavior: 'smooth'
        });
    }, [isRendered]);

    useEffect(() => {
        if (selectedTab !== -1) {
            if (!pendingIncomingSchemeRef.current) {
                _orderList();
            }
            setLoader(true);
        }
    }, [selectedTab]);

    useEffect(() => {
        if (state !== null) {
            const nextTab = getActiveTransactionTab(state.tab_index);
            const tabChanged = selectedTab !== -1 && selectedTab !== nextTab;

            setSelectedTab(nextTab);
            setCurrentPage(state.current_page || 1);

            if (tabChanged) {
                setSearchValue("");
                setSearched(false);
                setOrderDate(null);
                setSelectedOrderStatus(orderFilterData.order_status?.[0] || null);
                setRayiPaymentStatus(orderFilterData.payment_status?.[0] || null);
                setSelectedScheme(orderFilterData.scheme?.[0] || null);
                setSelectedFromScheme(orderFilterData.scheme?.[0] || null);
                setSelectedToScheme(orderFilterData.scheme?.[0] || null);
            } else if (state.rayi_customer_id || state.query) {
                setSearchValue(state.rayi_customer_id || state.query);
                setSearched(true);
            }

            pendingIncomingSchemeRef.current = hasIncomingSchemeFilter(state);

            if (!tabChanged && (state.mf_id != null || state.scheme || state.target_mf_id || state.from_scheme || state.to_scheme)) {
                setToggleFilter(true);
            }
        } else if (selectedTab === -1) {
            setSelectedTab(1);
            setCurrentPage(1);
        }
    }, [state]);

    // Restore tab_index only when missing (e.g. after modal history), never overwrite an intentional tab change
    useEffect(() => {
        if (selectedTab === -1) return;
        if (state?.tab_index != null) return;

        navigate(`/other-transactions`, {
            replace: true,
            state: {
                ...(state || {}),
                tab_index: selectedTab,
                current_page: currentPage || 1,
            },
        });
    }, [selectedTab, state?.tab_index]);

    useEffect(() => {
        if (selectedTransaction) {
            const modalId = selectedTab === 2 || selectedTab === 3
                ? 'systematic-plan-order-modal'
                : isNfoTab(selectedTab)
                    ? 'nfo-detail-modal'
                    : 'other-transaction-detail-modal';
            const detailModal = new Modal(document.getElementById(modalId));
            detailModal.show();
        }
    }, [selectedTransaction]);

    // Tab change is handled from left navigation
    // const _handleTab = (tab_id) => {
    //     if (TAB_TITLES.find((tab) => tab.id === tab_id)?.disabled) {
    //         return;
    //     }
    //     navigate(`/other-transactions`, {
    //         state: {
    //             tab_index: tab_id,
    //             current_page: 1
    //         }
    //     });
    //     setSelectedOrderStatus(orderFilterData.order_status?.[0] || null);
    //     setRayiPaymentStatus(orderFilterData.payment_status?.[0] || null);
    //     setOrderDate(null);
    //     setToggleFilter(false);
    //     setSearchValue("");
    // };

    const _handleClear = () => {
        setSelectedOrderStatus(orderFilterData.order_status?.[0] || null);
        setOrderDate(null);
        setRayiPaymentStatus(orderFilterData.payment_status?.[0] || null);
        setSelectedScheme(orderFilterData.scheme?.[0] || null);
        setSelectedFromScheme(orderFilterData.scheme?.[0] || null);
        setSelectedToScheme(orderFilterData.scheme?.[0] || null);

        navigate(`/other-transactions`, {
            state: {
                tab_index: selectedTab,
                current_page: 1
            }
        });

        isClearRef.current = true;
    };

    const _handleTablePageChange = (page_num) => {
        _orderList(page_num);

        navigate(`/other-transactions`, {
            state: {
                tab_index: selectedTab,
                current_page: page_num
            }
        });

        if (scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: "smooth", block: 'start' });
        }
        setLoader(true);
    };

    const _handleNavigateDetail = (id) => {
        navigate(`/customers/${id}`);
    };

    const _handleToggleFilter = () => {
        setToggleFilter(prev => !prev);
    };

    const _handleFilter = () => {
        if (!selectedOrderStatus && !rayiPaymentStatus) {
            toast.dismiss();
            toast.success("Please select at least one filter", { type: "error" });
        } else {
            setLoader(true);
            _orderList(1);
        }
    };

    const _handleSelectFilter = (selected, type) => {
        setCurrentPage(1);
        if (type === 1) {
            setRayiPaymentStatus(selected);
        } else {
            setSelectedOrderStatus(selected);
        }
        isClearRef.current = false;
    };

    const _onSearch = () => {
        setSearched(true);
        setLoader(true);
        _orderList(1);
    };

    const _handleSearchClear = () => {
        setSearched(false);
        setSearchValue("");
        if (searched === true) {
            setLoader(true);
            _orderList(1, "");
        }
    };

    const TRANSACTION_COLUMNS = getOtherTransactionsListColumns({
        includeCustomerColumns: true,
        onCustomerClick: _handleNavigateDetail,
        onViewDetails: setSelectedTransaction,
        tabId: selectedTab,
    });

    const renameIdsToValues = (data) => {
        const updatedData = {};

        Object.keys(data).forEach((key) => {
            if (!Array.isArray(data[key])) {
                updatedData[key] = data[key];
                return;
            }

            updatedData[key] = data[key].map((item) => {
                if (item.hasOwnProperty('id')) {
                    return {
                        ...item,
                        value: item.id,
                        id: undefined,
                    };
                }
                return item;
            }).map(({ id, ...rest }) => rest);
        });

        return updatedData;
    };

    const _getFilterValues = () => {
        let updated_payment_status = [];
        let updated_order_status = [];

        if (rayiPaymentStatus !== null && rayiPaymentStatus?.value !== null) {
            updated_payment_status = rayiPaymentStatus.filter(item => item?.value != null).map((value) => value.value);
        }

        if (selectedOrderStatus !== null && selectedOrderStatus?.value !== null) {
            updated_order_status = selectedOrderStatus.filter(item => item?.value != null).map((value) => value.value);
        }

        const order_start_date = orderDate === null ? null : format(new Date(orderDate[0]), 'dd-MM-yyyy');
        const order_end_date = orderDate === null ? null : format(new Date(orderDate[1]), 'dd-MM-yyyy');

        return {
            updated_payment_status,
            updated_order_status,
            order_start_date,
            order_end_date,
        };
    };

    const _filterOrder = () => {
        let is_post = false;
        let url = 'orders/filter-data';
        APIService(is_post, url).then((response) => {
            const schemeIds = {};

            if (response.status_code === 200) {
                const transformedData = renameIdsToValues(response.data);
                const schemeOptions = transformedData.scheme || [];
                setOrderFilterData(transformedData);
                setSelectedOrderStatus(response.data.order_status[0]);
                setRayiPaymentStatus(response.data.payment_status[0]);

                const incomingScheme = isSchemeFilterTab(state?.tab_index) && (state?.mf_id != null || state?.scheme);
                const incomingStp = state?.tab_index === 3 && (state?.mf_id != null || state?.from_scheme || state?.target_mf_id != null || state?.to_scheme);
                const nextScheme = incomingScheme
                    ? (resolveSchemeOption(schemeOptions, state?.mf_id, state?.scheme) || schemeOptions[0] || null)
                    : (schemeOptions[0] || null);
                const nextFromScheme = incomingStp
                    ? (resolveSchemeOption(schemeOptions, state?.mf_id, state?.from_scheme) || schemeOptions[0] || null)
                    : (schemeOptions[0] || null);
                const nextToScheme = incomingStp
                    ? (resolveSchemeOption(schemeOptions, state?.target_mf_id, state?.to_scheme) || schemeOptions[0] || null)
                    : (schemeOptions[0] || null);

                setSelectedScheme(nextScheme);
                setSelectedFromScheme(nextFromScheme);
                setSelectedToScheme(nextToScheme);

                if (incomingScheme || incomingStp) {
                    schemeIds.tab = getActiveTransactionTab(state.tab_index);
                    schemeIds.mf_id = incomingScheme ? (nextScheme?.value ?? null) : (nextFromScheme?.value ?? null);
                    schemeIds.target_mf_id = incomingStp ? (nextToScheme?.value ?? null) : null;
                }
            } else {
                setOrderFilterData([]);
            }
            setFilterLoader(false);

            if (pendingIncomingSchemeRef.current) {
                pendingIncomingSchemeRef.current = false;
                _orderList(state?.current_page || 1, state?.rayi_customer_id || state?.query || "", schemeIds);
            }
        });
    };

    const _orderList = (page = currentPage, search_value = searchValue, schemeIds = {}) => {
        const { updated_payment_status, updated_order_status, order_start_date, order_end_date } = _getFilterValues();
        const activeTab = schemeIds.tab ?? selectedTab;

        let is_post = true;
        let url = 'orders/list';
        let request = {
            "query": search_value,
            "page_num": page,
            "page_size": 10,
            "investment_type": getListInvestmentType(activeTab),
            "type": getListOrderType(activeTab),
            "order_status": updated_order_status,
            "payment_status": updated_payment_status,
            "order_start_date": order_start_date,
            "order_end_date": order_end_date,
            "mf_id": isSchemeFilterTab(activeTab) ? (schemeIds.mf_id !== undefined ? schemeIds.mf_id : (selectedScheme?.value ?? state?.mf_id ?? null))
                : activeTab === 3 ? (schemeIds.mf_id !== undefined ? schemeIds.mf_id : (selectedFromScheme?.value ?? state?.mf_id ?? null))
                    : null,
            "target_mf_id": activeTab === 3 ? (schemeIds.target_mf_id !== undefined ? schemeIds.target_mf_id : (selectedToScheme?.value ?? state?.target_mf_id ?? null)) : null
        };

        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {
                const updated_data = response.data.orders.map((item, index) => ({
                    ...item,
                    _index: index
                }));

                setOrderListData({
                    ...response.data,
                    orders: updated_data
                });
            } else {
                setOrderListData({ orders: [] });
            }
            setLoader(false);
            isClearRef.current = false;
        });
    };

    const _downloadOrder = () => {
        const { updated_payment_status, updated_order_status, order_start_date, order_end_date } = _getFilterValues();

        let is_post = true;
        let url = 'orders/order-download';
        let request = {
            "query": searchValue === "" ? null : searchValue,
            "investment_type": getListInvestmentType(selectedTab),
            "type": getListOrderType(selectedTab),
            "order_status": updated_order_status,
            "payment_status": updated_payment_status,
            "order_start_date": order_start_date,
            "order_end_date": order_end_date,
            "mf_id": isSchemeFilterTab(selectedTab) ? (selectedScheme?.value ?? state?.mf_id ?? null)
                : selectedTab === 3 ? (selectedFromScheme?.value ?? state?.mf_id ?? null)
                    : null,
            "target_mf_id": selectedTab === 3 ? (selectedToScheme?.value ?? state?.target_mf_id ?? null) : null
        };

        setDownloadLoader(true);
        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {
                toast.dismiss();
                toast.success("The document has been sent to your email.", {
                    type: "success",
                });
            } else {
                toast.dismiss();
                toast.error(response.message, { type: "error" });
            }
            setDownloadLoader(false);
        });
    };

    return (
        <section className='e-section-height pb-5'>
            <div className='e-bg-lavender padding-52px-lr pt-2 padding-112px-bottom'>
                <h3 className='mb-0 e-text-chinese-black e-primary-font e-font-24 e-font-weight-600 line-height-24px'>
                    {isNfoTab(selectedTab) ? "NFO" : (TAB_TITLES.find((tab) => tab.id === selectedTab)?.label || "Switch")}
                </h3>
            </div>

            <div className='padding-52px-lr margin--80px-top'>
                <div className='e-bg-ghost-white border-radius-16 pb-4'>
                    <div ref={scrollRef} className='padding-32px-lr padding-32px-top'>
                        {/* Tab handled from left navigation (Switch / SWP / STP / NFO) */}
                        {/* <div className='d-flex'>
                            <CustomTab data={TAB_TITLES}
                                selectedTab={selectedTab}
                                tooltip={false}
                                handleClick={_handleTab} />
                        </div> */}

                        <div className='d-flex align-items-center pb-2 justify-content-end gap-16px'>
                            {hasOrderDownloadAccess &&
                                <SecondaryButton name="Download List"
                                    disabled={loader === true || orderListData?.orders?.length === 0}
                                    className={downloadLoader ? "w-129px h-42px" : ""}
                                    loader={downloadLoader}
                                    onClick={_downloadOrder} />
                            }

                            <CustomSearchInput placeholder="Search with order ID, investor ID or MFS ID"
                                type="text"
                                value={searchValue}
                                onSearch={() => _onSearch()}
                                onClear={_handleSearchClear}
                                className="w-370px"
                                onSearchClear={() => _handleSearchClear()}
                                onKeyPress={() => _onSearch()}
                                handleChange={(e) => {
                                    setSearchValue(e);
                                    if (e.length === 0) {
                                        _handleSearchClear();
                                    }
                                }}
                            />

                            <div className={`${toggleFilter ? 'e-bg-blue-violet' : ''} padding-10px-all border-radius-8 e-cursor-pointer e-border-1px-light-purple cursor-pointer`}
                                onClick={filterLoader === false && _handleToggleFilter}>
                                <Icon icon='filter'
                                    size='1.25rem'
                                    className="d-flex"
                                    color={toggleFilter && color.white} />
                            </div>
                        </div>
                    </div>

                    <div className={`e-filter-content ${toggleFilter ? 'expanded ' : 'collapsed '}`}>
                        <div className='e-bg-main-white pt-3 pb-4 padding-32px-lr'>
                            <div className='d-flex justify-content-end'>
                                <TextButton name="Clear all"
                                    onClick={_handleClear}
                                    disabled={isClearRef.current === true}
                                    cursorPointer={isClearRef.current === true ? 'cursor-not-allowed' : 'cursor-pointer'}
                                />
                            </div>
                            <div className='d-flex gap-3 padding-32px-bottom flex-wrap'>
                                {isNfoTab(selectedTab) ?
                                    <>
                                        <CustomSelectBox title='Rayi Order Status'
                                            placeholder='Select'
                                            controlWidth='10rem'
                                            options={orderFilterData.order_status}
                                            isSearchable={false}
                                            value={selectedOrderStatus}
                                            isMulti={true}
                                            type={3}
                                            onChange={(data) => _handleSelectFilter(data, 2)} />

                                        <CustomSelectBox title='Rayi Payment Status'
                                            placeholder='Select'
                                            controlWidth='10rem'
                                            options={orderFilterData.payment_status}
                                            isSearchable={false}
                                            isMulti={true}
                                            type={3}
                                            value={rayiPaymentStatus}
                                            onChange={(data) => _handleSelectFilter(data, 1)} />

                                        <DateRangePicker label="Order date"
                                            placeholder="Select"
                                            value={orderDate}
                                            handleClear={() => setOrderDate(null)}
                                            handleChange={(date) => setOrderDate(date)} />
                                    </>
                                    :
                                    <>
                                        {isSchemeFilterTab(selectedTab) &&
                                            <CustomSelectBox title='Scheme'
                                                placeholder='Select'
                                                controlWidth='16rem'
                                                loader={filterLoader}
                                                options={orderFilterData.scheme}
                                                isSearchable={true}
                                                value={selectedScheme}
                                                onChange={(data) => {
                                                    setSelectedScheme(data);
                                                    setCurrentPage(1);
                                                    isClearRef.current = false;
                                                }} />
                                        }

                                        {selectedTab === 3 &&
                                            <>
                                                <CustomSelectBox title='From Scheme'
                                                    placeholder='Select'
                                                    controlWidth='16rem'
                                                    loader={filterLoader}
                                                    options={orderFilterData.scheme}
                                                    isSearchable={true}
                                                    value={selectedFromScheme}
                                                    onChange={(data) => {
                                                        setSelectedFromScheme(data);
                                                        setCurrentPage(1);
                                                        isClearRef.current = false;
                                                    }} />

                                                <CustomSelectBox title='To Scheme'
                                                    placeholder='Select'
                                                    controlWidth='16rem'
                                                    loader={filterLoader}
                                                    options={orderFilterData.scheme}
                                                    isSearchable={true}
                                                    value={selectedToScheme}
                                                    onChange={(data) => {
                                                        setSelectedToScheme(data);
                                                        setCurrentPage(1);
                                                        isClearRef.current = false;
                                                    }} />
                                            </>
                                        }

                                        <CustomSelectBox title='Rayi Order Status'
                                            placeholder='Select'
                                            controlWidth='10rem'
                                            options={orderFilterData.order_status}
                                            isSearchable={false}
                                            value={selectedOrderStatus}
                                            isMulti={true}
                                            type={3}
                                            onChange={(data) => _handleSelectFilter(data, 2)} />

                                        <DateRangePicker label="Order date"
                                            placeholder="Select"
                                            value={orderDate}
                                            handleClear={() => setOrderDate(null)}
                                            handleChange={(date) => setOrderDate(date)} />
                                    </>
                                }
                            </div>

                            <PrimaryButton name='Filter now'
                                paddingBlock='padding-12px-tb padding-44px-lr'
                                disabled={loader === true}
                                onClick={_handleFilter} />
                        </div>
                    </div>

                    <div className='pt-1 padding-32px-lr d-flex gap-1 align-items-center justify-content-end'>
                        <div>
                            <img src={require("../../Assets/Image/scroll-animation.gif")}
                                draggable={false}
                                className='e-rotate-270'
                                width={24}
                                height={24}
                                alt='scroll' />
                        </div>
                        <p className='mb-0 e-text-bleu-de-france e-alt-font e-font-12 e-font-weight-500 line-height-12px'>
                            Scroll horizontally for view more information
                        </p>
                    </div>

                    {loader ?
                        <Loader className="pt-5 min-vh-50" />
                        :
                        orderListData?.orders?.length > 0 ?
                            <div className={`e-data-table-wrapper-default-updated pt-3 padding-32px-`}>
                                <CustomDataTable columns={TRANSACTION_COLUMNS}
                                    data={orderListData.orders}
                                    pagination={true}
                                    paginationPerPage={10}
                                    paginationDefaultPage={orderListData.current_page}
                                    paginationTotalRows={orderListData.total_count}
                                    lastPage={orderListData.total_page}
                                    onChangePage={_handleTablePageChange}
                                />
                            </div>
                            :
                            <EmptyScreen
                                image={require('../../Assets/Image/NotFound/no-data.png')}
                                title='No results found'
                                description={`We couldn't find anything that matches your search. Try checking your spelling, using different keywords, or exploring popular funds.`}
                            />
                    }
                </div>
            </div>

            {selectedTransaction && (selectedTab === 2 || selectedTab === 3) && (
                <SystematicPlanOrderModal data={selectedTransaction || {}}
                    type={selectedTab === 3 ? "STP" : "SWP"}
                    handleClose={() => {
                        setTimeout(() => {
                            setSelectedTransaction(null);
                        }, 200);
                    }} />
            )}

            {selectedTransaction && selectedTab === 1 && (
                <OtherTransactionDetailModal data={selectedTransaction || {}}
                    handleClose={() => {
                        setTimeout(() => {
                            setSelectedTransaction(null);
                        }, 200);
                    }} />
            )}

            {selectedTransaction && isNfoTab(selectedTab) && (
                <NfoDetailModal data={selectedTransaction || {}}
                    handleClose={() => {
                        setTimeout(() => {
                            setSelectedTransaction(null);
                        }, 200);
                    }} />
            )}
        </section>
    );
};

export default OtherTransactionsList;
